import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ADMIN_API } from "../../../common/common";
import { Logout } from "../../../common/Funcation";
import { postApi } from "../../../service";
import { openRiskPlayerBetsWindow } from "./RiskPlayerBetsPopup";

interface DownlineRow {
  userId: string;
  userName: string;
  agentLevel: string;
  isPlayer: boolean;
  left: number;
  right: number;
  draw: number;
  whoAdd?: any[];
}

interface StackItem {
  parentId: string;
  label: string;
}

function formatNum(value: number | undefined) {
  return (Number(value) || 0).toFixed(2);
}

function plClass(value: number | undefined) {
  return (Number(value) || 0) < 0 ? "is-loss" : "";
}

function isDrawLabel(name: string) {
  const normalized = String(name || "")
    .toLowerCase()
    .replace(/\./g, "")
    .trim();
  return normalized === "the draw" || normalized === "draw";
}

function parseTeamNames(eventName: string, apiTeams: string[] = []) {
  const teams = (apiTeams || [])
    .map((team) => String(team || "").trim())
    .filter(Boolean);
  if (teams.length >= 2) return teams;
  const parts = eventName
    .split(/\s+v(?:s)?\.?\s+/i)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts : ["1", "2"];
}

async function loadDownlinePl(options: {
  matchId: string;
  parentId?: string;
  betType: "odds" | "bookMark";
  eventName?: string;
}) {
  const { matchId, parentId, betType, eventName } = options;

  try {
    const response = await postApi({
      api: ADMIN_API.RISK_DOWNLINE_PL,
      value: {
        id: matchId,
        parentId: parentId || "",
        betType: [betType],
      },
    });
    const data = response.data.data;
    if (data && Array.isArray(data.rows)) {
      return data;
    }
  } catch {
    // Fall back to existing admin book API (first level only on remote).
  }

  if (parentId) {
    return {
      eventName: eventName || "",
      marketName: betType === "bookMark" ? "Book Maker" : "Odds",
      teamNames: parseTeamNames(eventName || ""),
      rows: [],
      totals: { left: 0, right: 0, draw: 0 },
    };
  }

  const response = await postApi({
    api: ADMIN_API.RISK_BET_LIST,
    value: {
      id: matchId,
      betType: [betType],
    },
  });
  const data = response.data.data || {};
  const adminBook = data.adminBook || [];
  const betsHistory = data.betsHistory || [];
  const sportInfo = data.sportInfo;
  const userMeta = new Map<
    string,
    { userId: string; agentLevel: string; whoAdd?: any[] }
  >();

  betsHistory.forEach((bet: any) => {
    const name = bet?.userId?.user_name;
    if (!name) return;
    const upline = Array.isArray(bet.userId?.whoAdd)
      ? bet.userId.whoAdd
      : Array.isArray(bet.userId?.uperLineInfo)
        ? bet.userId.uperLineInfo
        : undefined;

    userMeta.set(name, {
      userId: String(bet.userId._id || ""),
      agentLevel: bet.userId.agent_level || "PL",
      whoAdd: upline,
    });
  });

  const rows: DownlineRow[] = adminBook
    .filter((row: any) => row.userName && row.userName !== "admin")
    .map((row: any) => {
      const meta = userMeta.get(row.userName);
      const agentLevel = meta?.agentLevel || "PL";
      return {
        userId: meta?.userId || "",
        userName: row.userName,
        agentLevel,
        isPlayer: String(agentLevel).toUpperCase() === "PL",
        left: Number(row.left) || 0,
        right: Number(row.right) || 0,
        draw: Number(row.draw) || 0,
        whoAdd: meta?.whoAdd,
      };
    });

  const totals = rows.reduce(
    (acc, row) => {
      acc.left += row.left;
      acc.right += row.right;
      acc.draw += row.draw;
      return acc;
    },
    { left: 0, right: 0, draw: 0 }
  );

  const event = sportInfo?.name || eventName || "";
  const teamNames =
    sportInfo?.winnerSelection?.length >= 2
      ? sportInfo.winnerSelection
      : parseTeamNames(event);

  return {
    eventName: event,
    marketName: betType === "bookMark" ? "Book Maker" : "Odds",
    teamNames,
    rows,
    totals: {
      left: Number(totals.left.toFixed(2)),
      right: Number(totals.right.toFixed(2)),
      draw: Number(totals.draw.toFixed(2)),
    },
  };
}

function RiskDownlinePLPopup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get("matchId") || "";
  const eventNameParam = searchParams.get("eventName") || "";
  const betTypeParam =
    searchParams.get("betType") === "bookMark" ? "bookMark" : "odds";

  const [stack, setStack] = useState<StackItem[]>([
    { parentId: "", label: "Root" },
  ]);
  const [rows, setRows] = useState<DownlineRow[]>([]);
  const [teamNames, setTeamNames] = useState<string[]>(["1", "2"]);
  const [eventName, setEventName] = useState(eventNameParam);
  const [marketName, setMarketName] = useState(
    betTypeParam === "bookMark" ? "Book Maker" : "Odds"
  );
  const [totals, setTotals] = useState({ left: 0, right: 0, draw: 0 });
  const [loading, setLoading] = useState(true);

  const currentParentId = stack[stack.length - 1]?.parentId || "";

  useEffect(() => {
    document.title = `${eventNameParam || "Downline P/L"}`;
  }, [eventNameParam]);

  useEffect(() => {
    if (!matchId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const data = await loadDownlinePl({
          matchId,
          parentId: currentParentId || undefined,
          betType: betTypeParam,
          eventName: eventNameParam,
        });
        if (cancelled) return;
        setRows(data.rows || []);
        setEventName(data.eventName || eventNameParam);
        setMarketName(data.marketName || marketName);
        setTeamNames(
          parseTeamNames(data.eventName || eventNameParam, data.teamNames || [])
        );
        setTotals(data.totals || { left: 0, right: 0, draw: 0 });
      } catch (err: any) {
        if (!cancelled) {
          setRows([]);
          setTotals({ left: 0, right: 0, draw: 0 });
        }
        if (err?.response?.data?.statusCode === 401) {
          Logout();
          navigate("/login");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [matchId, currentParentId, betTypeParam, eventNameParam, navigate]);

  const plColumns = useMemo(() => {
    const names = teamNames.filter(Boolean);
    const drawName = names.find((name) => isDrawLabel(name)) || "The Draw";
    const sides = names.filter((name) => !isDrawLabel(name));
    const leftName = sides[0] || "1";
    const rightName = sides[1] || "2";
    const includeDraw =
      names.some((name) => isDrawLabel(name)) ||
      names.length > 2 ||
      Math.abs(Number(totals.draw) || 0) > 0.001 ||
      rows.some((row) => Math.abs(Number(row.draw) || 0) > 0.001);

    if (!includeDraw) {
      return [
        { key: "left" as const, label: leftName },
        { key: "right" as const, label: rightName },
      ];
    }

    // Reference order is team 1, The Draw, team 2.
    return [
      { key: "left" as const, label: leftName },
      { key: "draw" as const, label: drawName },
      { key: "right" as const, label: rightName },
    ];
  }, [teamNames, totals.draw, rows]);

  const onDownlineClick = (row: DownlineRow) => {
    const level = String(row.agentLevel || "").toUpperCase();
    const isPlayer = row.isPlayer || level === "PL" || level === "CL";
    if (isPlayer) {
      if (!row.userId) return;
      const stackUpline =
        stack.length > 1
          ? stack.slice(1).map((s) => ({
            _id: s.parentId,
            user_name: s.label,
            agent_level: "A",
          }))
          : undefined;

      openRiskPlayerBetsWindow({
        userId: row.userId,
        userName: row.userName,
        agentLevel: row.agentLevel || "PL",
        whoAdd: row.whoAdd || stackUpline,
      });
      return;
    }

    if (!row.userId) return;
    setStack((prev) => [
      ...prev,
      { parentId: row.userId, label: row.userName },
    ]);
  };

  const goToStackIndex = (index: number) => {
    setStack((prev) => prev.slice(0, index + 1));
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", background: "#fff", height: "100vh" }}>
      <div className="risk-downline-pl-page">
        <div className="risk-downline-pl-head">
          <div className="risk-downline-pl-title">
            <h5>{eventName || "Downline P/L"}</h5>
            <span className="risk-market-badge">{marketName}</span>

          </div>
          <button
            type="button"
            className="risk-bet-details-close"
            onClick={() => window.close()}
          >
            <span aria-hidden="true">×</span> Close
          </button>
        </div>

        {stack.length > 1 && (
          <div className="risk-downline-pl-crumbs" style={{ padding: "8px 12px 0 12px" }}>
            {stack.map((item, index) => (
              <button
                key={`${item.parentId}-${index}`}
                type="button"
                className="risk-downline-crumb"
                onClick={() => goToStackIndex(index)}
              >
                {index === 0 ? "My Downline" : item.label}
                {index < stack.length - 1 ? " › " : ""}
              </button>
            ))}
          </div>
        )}

        <div className="risk-downline-pl-body">
          <table className="risk-downline-pl-table">
            <thead>
              <tr>
                <th className="align-L" rowSpan={2}>
                  Downline
                </th>
                <th className="align-C pl-group" style={{ width: "18%", background: "#BEDDF4" }} colSpan={plColumns.length}>
                  Player P/L
                </th>
              </tr>
              <tr>
                {plColumns.map((column) => (
                  <th key={column.key} className="align-C pl-sub" style={{ background: "#BEDDF4" }}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={1 + plColumns.length} className="align-C">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={1 + plColumns.length} className="align-C">
                    No Data
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((row, index) => (
                    <tr key={`${row.userId}-${row.userName}-${index}`}>
                      <td className="align-L">
                        <span style={{ width: "30px", marginRight: "7px" }}>{index + 1}..</span>
                        <button
                          type="button"
                          className="risk-downline-link"
                          onClick={() => onDownlineClick(row)}
                        >
                          {row.userName}
                        </button>
                      </td>
                      {plColumns.map((column) => (
                        <td
                          key={column.key}
                          className={`align-C ${plClass(row[column.key])}`}
                        >
                          {formatNum(row[column.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td className="align-L">
                      <strong>Total</strong>
                    </td>
                    {plColumns.map((column) => (
                      <td
                        key={column.key}
                        className={`align-C ${plClass(totals[column.key])}`}
                      >
                        <strong>{formatNum(totals[column.key])}</strong>
                      </td>
                    ))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RiskDownlinePLPopup;

function openPopupWindow(url: string, windowName: string) {
  const width = 920;
  const height = 640;
  const left = window.screenX || window.screenLeft || 0;
  const top = window.screenY || window.screenTop || 0;
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
    "toolbar=no",
    "menubar=no",
    "location=no",
    "status=no",
  ].join(",");
  const popup = window.open(url, windowName, features);
  if (popup) popup.focus();
  return popup;
}

export function openRiskDownlinePLWindow(options: {
  matchId: string;
  eventName?: string;
  betType?: "odds" | "bookMark";
  gameId?: number | string;
  marketId?: string;
}) {
  const params = new URLSearchParams({
    matchId: String(options.matchId),
    betType: options.betType || "odds",
  });
  if (options.eventName) params.set("eventName", options.eventName);
  if (options.gameId != null && options.gameId !== "") {
    params.set("gameId", String(options.gameId));
  }
  if (options.marketId) params.set("marketId", String(options.marketId));

  return openPopupWindow(
    `/risk-downline-pl?${params.toString()}`,
    `RiskDownlinePL_${options.matchId}`
  );
}
